import { Trans } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { FormAction } from '@anytag/shared-types';
import { TextField } from '@anytag/shared-molecules';
import { useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { localizedDateFormatter, isEqualSets, cn } from '@anytag/shared-utils';
import { UNEXPECTED_ERROR, AFFILIATE_CAMPAIGN_TYPES } from '@anytag/shared-constants';
import { Button, Notice, BaseModal, ButtonProps, Form, ModalFooter, ModalBody } from '@anytag/shared-atoms';
import {
  UserRoles,
  MarketplaceDetail,
  MarketplaceCampaignStatus,
  useApproveMarketplaceMutation,
  useRejectMarketplaceCampaignMutation
} from '@anytag/graphql';
import { useSetHubspotDealIntoUrl } from '../MarketplaceCampaignForms/hooks';

interface RejectFormValues {
  reason: string;
}

interface Props {
  marketplace: MarketplaceDetail;
}

export const useUpdateMarketplaceStatus = ({
  marketplace: {
    id,
    status,
    title,
    rejectedInfo,
    campaignType,
    hubspotDealIds,
    isPostTracking,
    campaignCreator,
    allowNewInfluencer,
    isAllowMultiplePosts,
    isAutoInfluencerApproval
  }
}: Props) => {
  const rejectDialog = useToggle();
  const approveDialog = useToggle();
  const setDealIdDialog = useToggle();

  const { isAdminStaff, isAdvertiser } = useUserRoles();
  const { t, navigate, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { hubspotDealIdsFromUrl, unsavedChangesDialogOpen, setUnsavedChangesDialogOpen, setUnsavedChangesDialogClose } =
    useSetHubspotDealIntoUrl();

  const { callApproveMarketplace, loading: approving } = useApproveMarketplaceMutation({
    onCompleted: invalidateRouteLoader
  });
  const { callRejectMarketplaceCampaign, loading: rejecting } = useRejectMarketplaceCampaignMutation({
    onCompleted: invalidateRouteLoader
  });

  // Adv users can only approve campaign if sales&Infl PICS were added;
  // admin|staff users only can't approve if campaign is created by them and has no deal_id
  const needToSetDealIdBeforeApprove =
    isAdminStaff &&
    hubspotDealIds?.length === 0 &&
    campaignCreator?.role &&
    [UserRoles.ADMINISTRATOR, UserRoles.STAFF].includes(campaignCreator.role);

  const needsToSaveBeforeApprove =
    hubspotDealIdsFromUrl?.length &&
    (needToSetDealIdBeforeApprove ||
      (status === MarketplaceCampaignStatus.REVIEWING &&
        !isEqualSets(new Set(hubspotDealIdsFromUrl), new Set(hubspotDealIds))));

  const handleApproveMarketplaceClick = async () => {
    try {
      await callApproveMarketplace({ variables: { input: { id } } });

      enqueueSnackbar(t('succeededInUpdating', { name: title }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('failedToUpdate', { name: title }), { variant: 'error' });
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    } finally {
      approveDialog.setOff();
    }
  };
  const handleRejectMarketplaceClick: FormAction<RejectFormValues>['onSubmit'] = async ({ reason }) => {
    try {
      await callRejectMarketplaceCampaign({ variables: { input: { marketplaceId: id, reason } } });

      enqueueSnackbar(t('succeededInUpdating', { name: title }), { variant: 'success' }); // TODO: has been rejected!
    } catch (error) {
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    } finally {
      rejectDialog.setOff();
    }
  };

  const handleApproveButtonClick = () => {
    if (needsToSaveBeforeApprove) {
      setUnsavedChangesDialogOpen();
    } else if (needToSetDealIdBeforeApprove) {
      setDealIdDialog.setOn();
    } else {
      approveDialog.setOn();
    }
  };

  const hasApproveRejectNotice =
    isAdminStaff && campaignCreator?.role === UserRoles.ADVERTISER && status === MarketplaceCampaignStatus.REVIEWING;
  const rejectedByAdminStaff = status === MarketplaceCampaignStatus.REJECTED;
  const hasCustomApproveRejectWarningBar = hasApproveRejectNotice || (isAdvertiser && rejectedByAdminStaff);

  return {
    hasApproveRejectNotice,
    hasCustomApproveRejectWarningBar,
    ApproveButton: (props: ButtonProps) => (
      <Button
        variant="solid"
        loading={approving}
        css={{ minWidth: '200px', textTransform: 'uppercase' }}
        title={t(approving ? 'Button.Updating' : 'Button.Approve this campaign')}
        onClick={handleApproveButtonClick}
        {...props}
      />
    ),
    ApproveRejectWarningBar: () => (
      <>
        <Notice
          type="warning"
          className="mx-0 my-4"
          open={rejectedByAdminStaff}
          description={t(rejectedInfo?.reason || '')}
          title={
            <Trans
              i18nKey="CampaignIsRejectedByNameOnDate"
              components={{ span: <span css={{ fontWeight: 400 }} /> }}
              values={{
                name: isAdminStaff ? rejectedInfo?.operatedUser.name : 'AnyMind Staff',
                date: localizedDateFormatter(rejectedInfo?.operatedDate || '', 'yyyy/MM/dd')
              }}
            />
          }
        />

        <Notice
          type="info"
          className="mx-0 my-4"
          open={hasApproveRejectNotice}
          title={
            rejectedInfo ? (
              <Trans
                i18nKey="CampaignIsModifiedByNameOnDate"
                components={{ span: <span css={{ fontWeight: 400 }} /> }}
                values={{
                  name: campaignCreator?.name,
                  date: localizedDateFormatter(rejectedInfo?.operatedDate, 'yyyy/MM/dd')
                }}
              />
            ) : (
              t('This campaign is created by', { name: campaignCreator?.name })
            )
          }
          description={
            rejectedInfo
              ? `${t('TextForm.Reason for rejection')}: ${rejectedInfo?.reason || '-'}`
              : t('Please check the details and approve or reject this campaign')
          }
        >
          <div css={{ gap: '16px', display: 'flex' }}>
            <Button
              variant="solid"
              color="danger"
              loading={rejecting}
              title={t('Button.Reject')}
              onClick={rejectDialog.setOn}
              css={{ minWidth: '69px', padding: '0 16px' }}
            />
            <Button
              variant="solid"
              loading={approving}
              title={t('Button.Approve')}
              onClick={approveDialog.setOn}
              css={{ minWidth: '89px', padding: '0 16px' }}
            />
          </div>
        </Notice>
      </>
    ),
    DialogModals: (
      <>
        <BaseModal
          size="content"
          withCloseIcon={false}
          open={approveDialog.on}
          onClose={approveDialog.setOff}
          title={t('Check the campaign settings before you start campaign')}
          description={
            <Trans i18nKey="These settings cannot be edited once you approve this campaign" components={{ b: <b /> }} />
          }
          footer={{
            cancelButton: { onClick: approveDialog.setOff },
            submitButton: {
              loading: approving,
              onClick: handleApproveMarketplaceClick,
              title: t(approving ? 'Button.Updating' : 'Button.Approve')
            }
          }}
        >
          <ul
            className={cn(
              'bg-light-gray p-2',
              '[&_li]:text-base [&_li]:leading-5',
              '[&_li]:before:mr-1 [&_li]:before:align-top [&_li]:before:text-xxs [&_li]:before:leading-5 [&_li]:before:content-["•"]'
            )}
          >
            {AFFILIATE_CAMPAIGN_TYPES.includes(campaignType) ? (
              <li>
                {t(`Track creator's posts:`)}&nbsp;
                <span css={{ fontWeight: 600 }}>{t(isPostTracking ? 'On' : 'Off')}</span>
              </li>
            ) : null}
            <li>
              {t('How to approve influencers:')}&nbsp;
              <span css={{ fontWeight: 600 }}>{t(isAutoInfluencerApproval ? 'Auto-approve' : 'Manual-approve')}</span>
            </li>
            <li>
              {t('Multi-Posting for creator:')}&nbsp;
              <span css={{ fontWeight: 600 }}>{t(isAllowMultiplePosts ? 'Enable' : 'Disable')}</span>
            </li>
            <li>
              {t('Normal or Closed campaign:')}&nbsp;
              <span css={{ fontWeight: 600 }}>{t(allowNewInfluencer ? 'Normal' : 'Closed')}</span>
            </li>
          </ul>
        </BaseModal>

        <BaseModal
          open={rejectDialog.on}
          onClose={rejectDialog.setOff}
          description={t('rejectMkpCampaignReasonDescription')}
          title={t('Are you sure you want to reject the campaign?')}
          asChild
        >
          <Form.Provider<RejectFormValues> onSubmit={handleRejectMarketplaceClick}>
            <ModalBody>
              <TextField<keyof RejectFormValues> name="reason" title={t('Reason of rejection')} optional />
            </ModalBody>

            <ModalFooter
              cancelButton={{ onClick: rejectDialog.setOff }}
              submitButton={{ type: 'submit', color: 'danger', loading: rejecting, title: t('Button.Reject') }}
            />
          </Form.Provider>
        </BaseModal>

        <BaseModal
          open={setDealIdDialog.on}
          onClose={setDealIdDialog.setOff}
          title={t('Set HubSpot deal')}
          description={t('You need to set HubSpot deal in order to approve this campaign')}
          footer={{
            cancelButton: { onClick: setDealIdDialog.setOff },
            submitButton: {
              onClick: () => {
                navigate({ to: '/marketplace/$id', params: { id: id.toString() } });
                setDealIdDialog.setOff();
              }
            }
          }}
        />

        <BaseModal
          open={unsavedChangesDialogOpen}
          onClose={setUnsavedChangesDialogClose}
          title={t('Action required')}
          description={t('SaveChangesBeforeApproveDescription')}
          footer={{ submitButton: { onClick: setUnsavedChangesDialogClose } }}
        />
      </>
    )
  };
};
